import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  notification,
} from 'antd';
import KountaProductLinker from 'components/KountaProductLinker/KountaProductLinker';
import * as api from 'util/api';

const { Text } = Typography;

const STORES = ['GPNZ', 'GPAU'];
const STATUS_OPTIONS = [
  { value: 'needs_review', label: 'Needs review' },
  { value: 'linked', label: 'Linked' },
  { value: 'all', label: 'All' },
];
const NEEDS_OPTIONS = [
  { value: 'any', label: 'Any platform' },
  { value: 'cin7', label: 'Needs Cin7' },
  { value: 'kounta', label: 'Needs Kounta' },
  { value: 'shopify', label: 'Needs Shopify' },
];

function shopifyProductAdminHref(store, shopifyProductId) {
  if (shopifyProductId == null || shopifyProductId === '') return null;
  const slug = store === 'GPAU' ? 'garageproject-australia' : 'garage-project';
  return `https://${slug}.myshopify.com/admin/products/${shopifyProductId}`;
}

function dearProductHref(dearCode) {
  if (!dearCode) return null;
  return `https://inventory.dearsystems.com/Product#${dearCode}`;
}

function errorMessage(err, fallback) {
  return err?.response?.data?.error || err?.message || fallback;
}

function acceptShopifyProductId(summary) {
  return summary?.shopifyProductId || summary?.preferredProductId || null;
}

function candidateLabel(p) {
  const created = p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '';
  const variants = (p.variants || []).map((v) => v.variantTitle).filter(Boolean).join(', ');
  return [p.productTitle, p.productStatus, created].filter(Boolean).join(' — ')
    + (variants ? ` (${variants})` : '');
}

function shopifyStatusTag(status) {
  if (status === 'ambiguous') return <Tag color="orange">ambiguous</Tag>;
  if (status === 'manual') return <Tag color="blue">manual</Tag>;
  if (status === 'auto') return <Tag color="green">auto</Tag>;
  if (status === 'mixed') return <Tag color="purple">mixed</Tag>;
  if (status === 'needs_review') return <Tag color="orange">review</Tag>;
  if (status === 'linked') return <Tag color="green">linked</Tag>;
  if (!status) return <Tag>n/a</Tag>;
  return <Tag>{status}</Tag>;
}

function CellActions({
  acceptDisabled, pickDisabled, unlinkDisabled, saving, onAccept, onPick, onUnlink,
}) {
  return (
    <Space size={4} wrap>
      <Button
        size="small"
        type="primary"
        disabled={acceptDisabled || saving}
        onClick={onAccept}
      >
        Accept
      </Button>
      <Button size="small" disabled={pickDisabled || saving} onClick={onPick}>
        Pick…
      </Button>
      <Button
        size="small"
        danger
        disabled={unlinkDisabled || saving}
        onClick={onUnlink}
      >
        Unlink
      </Button>
    </Space>
  );
}

CellActions.propTypes = {
  acceptDisabled: PropTypes.bool,
  pickDisabled: PropTypes.bool,
  unlinkDisabled: PropTypes.bool,
  saving: PropTypes.bool,
  onAccept: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  onUnlink: PropTypes.func.isRequired,
};

CellActions.defaultProps = {
  acceptDisabled: false,
  pickDisabled: false,
  unlinkDisabled: false,
  saving: false,
};

const cin7SuggestionShape = PropTypes.shape({
  name: PropTypes.string,
  cin7Sku: PropTypes.string,
  dearCode: PropTypes.string,
  confidence: PropTypes.string,
});

const skuShape = PropTypes.shape({
  skuKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  skuName: PropTypes.string,
  format: PropTypes.string,
  containerType: PropTypes.string,
  cin7: PropTypes.shape({
    linked: PropTypes.bool,
    name: PropTypes.string,
    cin7Sku: PropTypes.string,
    dearCode: PropTypes.string,
    suggestion: cin7SuggestionShape,
  }),
  kounta: PropTypes.shape({
    linked: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      servingType: PropTypes.string,
      productName: PropTypes.string,
    })),
    candidates: PropTypes.arrayOf(PropTypes.shape({})),
    conflicts: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  shopify: PropTypes.shape({}),
  monday: PropTypes.shape({
    mondayId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    href: PropTypes.string,
  }),
});

const shopifyCellShape = PropTypes.shape({
  soldOnShopify: PropTypes.bool,
  linked: PropTypes.bool,
  shopifyProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  preferredProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  productTitle: PropTypes.string,
  preferredProductTitle: PropTypes.string,
  variantTitle: PropTypes.string,
  expectedTitle: PropTypes.string,
});

const shopifySummaryShape = PropTypes.shape({
  status: PropTypes.string,
  skuCount: PropTypes.number,
  linkedSkuCount: PropTypes.number,
  shopifyProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  preferredProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  productTitle: PropTypes.string,
  preferredProductTitle: PropTypes.string,
});

function Cin7Cell({
  sku, saving, onAccept, onPick, onUnlink,
}) {
  const { cin7 } = sku;
  const href = dearProductHref(cin7.dearCode);
  const title = cin7.linked
    ? (cin7.name || cin7.cin7Sku)
    : (cin7.suggestion?.name || cin7.suggestion?.cin7Sku);
  let body = <Text type="secondary">No match</Text>;
  if (cin7.linked) {
    body = (
      <Space size={4} wrap>
        {href ? (
          <a href={href} target="_blank" rel="noopener noreferrer">{title}</a>
        ) : <Text>{title || '—'}</Text>}
        {cin7.cin7Sku ? <Tag>{cin7.cin7Sku}</Tag> : null}
      </Space>
    );
  } else if (cin7.suggestion) {
    body = (
      <Space direction="vertical" size={0}>
        <Text>{title}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Suggested
          {cin7.suggestion.confidence ? ` · ${cin7.suggestion.confidence}` : ''}
        </Text>
      </Space>
    );
  }
  return (
    <Space direction="vertical" size={2}>
      {body}
      <CellActions
        acceptDisabled={!cin7.suggestion || cin7.linked}
        unlinkDisabled={!cin7.linked}
        saving={saving}
        onAccept={onAccept}
        onPick={onPick}
        onUnlink={onUnlink}
      />
    </Space>
  );
}

Cin7Cell.propTypes = {
  sku: skuShape.isRequired,
  saving: PropTypes.bool,
  onAccept: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  onUnlink: PropTypes.func.isRequired,
};

Cin7Cell.defaultProps = { saving: false };

function KountaCell({
  sku, saving, onAccept, onPick, onUnlink,
}) {
  const { kounta } = sku;
  const linkedN = (kounta.linked || []).length;
  const candN = (kounta.candidates || []).length;
  const conflictN = (kounta.conflicts || []).length;
  return (
    <Space direction="vertical" size={2}>
      {linkedN > 0 ? (
        <Space size={4} wrap>
          <Tag color="green">
            {linkedN}
            {' '}
            linked
          </Tag>
          {(kounta.linked || []).slice(0, 3).map((row) => (
            <Tag key={row.id}>{row.servingType || row.productName}</Tag>
          ))}
        </Space>
      ) : (
        <Text type="secondary">No links</Text>
      )}
      {candN > 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {candN}
          {' '}
          suggested
        </Text>
      ) : null}
      {conflictN > 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {conflictN}
          {' '}
          conflict
          {conflictN === 1 ? '' : 's'}
        </Text>
      ) : null}
      <CellActions
        acceptDisabled={candN === 0}
        unlinkDisabled={linkedN === 0}
        saving={saving}
        onAccept={onAccept}
        onPick={onPick}
        onUnlink={onUnlink}
      />
    </Space>
  );
}

KountaCell.propTypes = {
  sku: skuShape.isRequired,
  saving: PropTypes.bool,
  onAccept: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  onUnlink: PropTypes.func.isRequired,
};

KountaCell.defaultProps = { saving: false };

function ShopifySkuCell({
  cell, store, saving, onAccept, onPick, onUnlink,
}) {
  if (!cell?.soldOnShopify) {
    return <Text type="secondary">Not sold</Text>;
  }
  const id = cell.shopifyProductId || cell.preferredProductId;
  const title = cell.productTitle || cell.preferredProductTitle;
  const href = shopifyProductAdminHref(store, id);
  const variantLabel = cell.linked
    ? (cell.variantTitle || cell.expectedTitle)
    : cell.expectedTitle;
  return (
    <Space direction="vertical" size={2}>
      {id ? (
        <Space direction="vertical" size={0}>
          {href ? (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {title || id}
            </a>
          ) : <Text>{title || id}</Text>}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {cell.linked ? variantLabel : `Need ${variantLabel || 'variant'}`}
            {!cell.linked && cell.preferredProductId ? ' · Suggested' : ''}
          </Text>
        </Space>
      ) : (
        <Text type="secondary">
          {cell.expectedTitle ? `Need ${cell.expectedTitle}` : 'No match'}
        </Text>
      )}
      <CellActions
        acceptDisabled={!acceptShopifyProductId(cell)}
        unlinkDisabled={!cell.shopifyProductId}
        saving={saving}
        onAccept={onAccept}
        onPick={onPick}
        onUnlink={onUnlink}
      />
    </Space>
  );
}

ShopifySkuCell.propTypes = {
  cell: shopifyCellShape,
  store: PropTypes.string.isRequired,
  saving: PropTypes.bool,
  onAccept: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  onUnlink: PropTypes.func.isRequired,
};

ShopifySkuCell.defaultProps = { cell: null, saving: false };

function ShopifyBeerCell({
  summary, store, saving, onAccept, onPick, onUnlink,
}) {
  if (!summary || summary.skuCount === 0) {
    return <Text type="secondary">n/a</Text>;
  }
  const id = summary.shopifyProductId || summary.preferredProductId;
  const title = summary.productTitle || summary.preferredProductTitle;
  const href = shopifyProductAdminHref(store, id);
  return (
    <Space direction="vertical" size={2}>
      <Space size={4} wrap>
        {shopifyStatusTag(summary.status)}
        <Text type="secondary" style={{ fontSize: 12 }}>
          {summary.linkedSkuCount}
          /
          {summary.skuCount}
        </Text>
      </Space>
      {id ? (
        <Space direction="vertical" size={0}>
          {href ? (
            <a href={href} target="_blank" rel="noopener noreferrer">{title || id}</a>
          ) : <Text>{title || id}</Text>}
          {!summary.shopifyProductId && summary.preferredProductId ? (
            <Text type="secondary" style={{ fontSize: 12 }}>Suggested</Text>
          ) : null}
        </Space>
      ) : (
        <Text type="secondary">No match</Text>
      )}
      <CellActions
        acceptDisabled={!acceptShopifyProductId(summary)}
        unlinkDisabled={!summary.shopifyProductId}
        saving={saving}
        onAccept={onAccept}
        onPick={onPick}
        onUnlink={onUnlink}
      />
    </Space>
  );
}

ShopifyBeerCell.propTypes = {
  summary: shopifySummaryShape,
  store: PropTypes.string.isRequired,
  saving: PropTypes.bool,
  onAccept: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  onUnlink: PropTypes.func.isRequired,
};

ShopifyBeerCell.defaultProps = { summary: null, saving: false };

const PlatformLinksPanel = () => {
  const [status, setStatus] = useState('needs_review');
  const [needs, setNeeds] = useState('any');
  const [search, setSearch] = useState('');
  const [beers, setBeers] = useState([]);
  const [beerDetails, setBeerDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [pickShopify, setPickShopify] = useState(null);
  const [pickCin7, setPickCin7] = useState(null);
  const [pickKounta, setPickKounta] = useState(null);
  const [searchOptions, setSearchOptions] = useState([]);
  const [shopifyCandidates, setShopifyCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const searchTimerRef = useRef(null);
  const beerDetailsRef = useRef({});

  const loadReview = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getPlatformLinkReview({ status, needs });
      setBeers(data?.beers || []);
      setSelectedKeys([]);
    } catch (err) {
      notification.error({
        message: errorMessage(err, 'Failed to load platform links'),
      });
    } finally {
      setLoading(false);
    }
  }, [status, needs]);

  const patchBeerDetail = useCallback((id, next) => {
    beerDetailsRef.current = { ...beerDetailsRef.current, [id]: next };
    setBeerDetails(beerDetailsRef.current);
  }, []);

  const loadBeerDetail = useCallback(async (productMatrixId, { force = false } = {}) => {
    const id = productMatrixId == null ? '' : String(productMatrixId);
    if (!id) return null;
    const cached = beerDetailsRef.current[id];
    if (!force && cached?.skus && !cached.loading) return cached;
    patchBeerDetail(id, { ...(cached || {}), loading: true, error: null });
    try {
      const data = await api.getPlatformLinkBeer(id);
      patchBeerDetail(id, { loading: false, error: null, ...data });
      return data;
    } catch (err) {
      const message = errorMessage(err, 'Failed to load beer links');
      patchBeerDetail(id, { loading: false, error: message, skus: [] });
      notification.error({ message });
      return null;
    }
  }, [patchBeerDetail]);

  const refreshAfterWrite = useCallback(async (productMatrixId) => {
    await Promise.all([
      loadReview(),
      productMatrixId != null
        ? loadBeerDetail(productMatrixId, { force: true })
        : Promise.resolve(),
    ]);
  }, [loadReview, loadBeerDetail]);

  useEffect(() => {
    setExpandedKeys([]);
    beerDetailsRef.current = {};
    setBeerDetails({});
    loadReview();
  }, [loadReview]);

  const visibleBeers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return beers;
    return beers.filter((beer) => {
      if ((beer.productName || '').toLowerCase().includes(q)) return true;
      const skus = beerDetails[beer.productMatrixId]?.skus || [];
      return skus.some((sku) => (
        (sku.skuName || '').toLowerCase().includes(q)
        || (sku.format || '').toLowerCase().includes(q)
      ));
    });
  }, [beers, beerDetails, search]);

  const linkShopifyBeer = async (productMatrixId, store, shopifyProductId) => {
    const result = await api.setShopifyBeerLink({
      productMatrixId,
      store,
      shopifyProductId,
    });
    return result;
  };

  const handleShopifyAccept = async (beer, store) => {
    const summary = beer.shopify?.[store];
    const shopifyProductId = acceptShopifyProductId(summary);
    if (!shopifyProductId) {
      notification.warning({ message: `No Shopify candidate for ${store}` });
      return;
    }
    setSaving(true);
    try {
      const result = await linkShopifyBeer(
        beer.productMatrixId,
        store,
        shopifyProductId,
      );
      if (result?.failed > 0) {
        notification.warning({
          message: `Linked ${result.linked}/${result.linked + result.failed} ${store} SKU(s)`,
          description: (result.errors || [])[0],
        });
      } else {
        notification.success({
          message: `Accepted ${store} for ${result?.linked || 0} SKU(s)`,
        });
      }
      await refreshAfterWrite(beer.productMatrixId);
    } catch (err) {
      notification.error({ message: errorMessage(err, 'Failed to accept Shopify link') });
    } finally {
      setSaving(false);
    }
  };

  const handleShopifyUnlink = async (beer, store) => {
    setSaving(true);
    try {
      await linkShopifyBeer(beer.productMatrixId, store, null);
      notification.success({ message: `Unlinked ${store}` });
      await refreshAfterWrite(beer.productMatrixId);
    } catch (err) {
      notification.error({ message: errorMessage(err, 'Failed to unlink Shopify') });
    } finally {
      setSaving(false);
    }
  };

  const handleCin7Accept = async (beer, sku) => {
    const suggestion = sku.cin7?.suggestion;
    if (!suggestion?.cin7Sku) {
      notification.warning({ message: 'No Cin7 candidate to accept' });
      return;
    }
    setSaving(true);
    try {
      await api.setPlatformCin7Link({
        skuKey: sku.skuKey,
        cin7Sku: suggestion.cin7Sku,
        dearCode: suggestion.dearCode,
      });
      notification.success({ message: `Linked Cin7 ${suggestion.cin7Sku}` });
      await refreshAfterWrite(beer?.productMatrixId);
    } catch (err) {
      notification.error({ message: errorMessage(err, 'Failed to accept Cin7 link') });
    } finally {
      setSaving(false);
    }
  };

  const handleCin7Unlink = async (beer, sku) => {
    setSaving(true);
    try {
      await api.setPlatformCin7Link({
        skuKey: sku.skuKey,
        cin7Sku: null,
        dearCode: null,
      });
      notification.success({ message: 'Unlinked Cin7' });
      await refreshAfterWrite(beer?.productMatrixId);
    } catch (err) {
      notification.error({ message: errorMessage(err, 'Failed to unlink Cin7') });
    } finally {
      setSaving(false);
    }
  };

  const handleKountaAccept = async (beer, sku) => {
    setSaving(true);
    try {
      const result = await api.acceptPlatformKounta({ skuKey: sku.skuKey });
      const skipped = result?.skipped || 0;
      notification.success({
        message: `Linked ${result?.linked || 0} Kounta product(s)`,
        description: skipped ? `${skipped} skipped (already linked elsewhere)` : undefined,
      });
      await refreshAfterWrite(beer?.productMatrixId);
    } catch (err) {
      notification.error({ message: errorMessage(err, 'Failed to accept Kounta links') });
    } finally {
      setSaving(false);
    }
  };

  const handleKountaUnlink = async (beer, sku) => {
    setSaving(true);
    try {
      await api.unlinkPlatformKounta({ skuKey: sku.skuKey });
      notification.success({ message: 'Unlinked Kounta' });
      await refreshAfterWrite(beer?.productMatrixId);
    } catch (err) {
      notification.error({ message: errorMessage(err, 'Failed to unlink Kounta') });
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptSelected = async () => {
    const selected = visibleBeers.filter((b) => selectedKeys.includes(b.productMatrixId));
    if (selected.length === 0) return;
    setSaving(true);
    let accepted = 0;
    let failed = 0;
    try {
      const details = await Promise.all(
        selected.map((beer) => loadBeerDetail(beer.productMatrixId)),
      );
      for (let i = 0; i < selected.length; i += 1) {
        const beer = selected[i];
        for (let s = 0; s < STORES.length; s += 1) {
          const store = STORES[s];
          const id = acceptShopifyProductId(beer.shopify?.[store]);
          const alreadyLinked = beer.shopify?.[store]?.shopifyProductId;
          if (id && !alreadyLinked) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await linkShopifyBeer(beer.productMatrixId, store, id);
              accepted += 1;
            } catch (err) {
              failed += 1;
            }
          }
        }
        const skus = details[i]?.skus || [];
        for (let k = 0; k < skus.length; k += 1) {
          const sku = skus[k];
          if (!sku.cin7?.linked && sku.cin7?.suggestion?.cin7Sku) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await api.setPlatformCin7Link({
                skuKey: sku.skuKey,
                cin7Sku: sku.cin7.suggestion.cin7Sku,
                dearCode: sku.cin7.suggestion.dearCode,
              });
              accepted += 1;
            } catch (err) {
              failed += 1;
            }
          }
          if ((sku.kounta?.candidates || []).length > 0) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await api.acceptPlatformKounta({ skuKey: sku.skuKey });
              accepted += 1;
            } catch (err) {
              failed += 1;
            }
          }
        }
      }
      notification.success({
        message: `Accepted ${accepted} link(s)`,
        description: failed ? `${failed} failed` : undefined,
      });
      await Promise.all([
        loadReview(),
        ...selected.map((beer) => loadBeerDetail(beer.productMatrixId, { force: true })),
      ]);
    } finally {
      setSaving(false);
    }
  };

  const handleShopifySearch = (value) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const q = String(value || '').trim();
    if (!q || !pickShopify) {
      setSearchOptions([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await api.searchShopifySkuLinkProducts({
          store: pickShopify.store,
          q,
        });
        setSearchOptions((data?.products || []).map((p) => ({
          value: String(p.shopifyProductId),
          label: candidateLabel(p),
        })));
      } catch (err) {
        console.error('Shopify product search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleCin7Search = (value) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const q = String(value || '').trim();
    if (!q || !pickCin7) {
      setSearchOptions([]);
      return;
    }
    searchTimerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await api.searchPlatformCin7({
          q,
          containerType: pickCin7.sku?.containerType,
        });
        setSearchOptions((data?.products || []).map((p) => ({
          value: p.cin7Sku,
          label: [p.name, p.cin7Sku, p.confidence].filter(Boolean).join(' — '),
          dearCode: p.dearCode,
        })));
      } catch (err) {
        console.error('Cin7 search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleShopifyPick = async (shopifyProductId) => {
    if (!pickShopify) return;
    setSaving(true);
    try {
      await linkShopifyBeer(
        pickShopify.beer.productMatrixId,
        pickShopify.store,
        shopifyProductId,
      );
      notification.success({ message: `Linked ${pickShopify.store}` });
      setPickShopify(null);
      setSearchOptions([]);
      await refreshAfterWrite(pickShopify.beer.productMatrixId);
    } catch (err) {
      notification.error({ message: errorMessage(err, 'Failed to pick Shopify product') });
    } finally {
      setSaving(false);
    }
  };

  const openShopifyPick = async (beer, store) => {
    setPickShopify({ beer, store });
    setSearchOptions([]);
    setShopifyCandidates([]);
    try {
      const data = await api.getShopifySkuLinkCandidates({
        store,
        productMatrixId: beer.productMatrixId,
      });
      setShopifyCandidates((data?.candidates || []).map((p) => ({
        value: String(p.shopifyProductId),
        label: candidateLabel(p),
      })));
    } catch (err) {
      console.error('Shopify candidates failed:', err);
    }
  };

  const handleCin7Pick = async (cin7Sku, option) => {
    if (!pickCin7) return;
    setSaving(true);
    try {
      await api.setPlatformCin7Link({
        skuKey: pickCin7.sku.skuKey,
        cin7Sku,
        dearCode: option?.dearCode || null,
      });
      notification.success({ message: `Linked Cin7 ${cin7Sku}` });
      setPickCin7(null);
      setSearchOptions([]);
      await refreshAfterWrite(pickCin7.beer?.productMatrixId);
    } catch (err) {
      notification.error({ message: errorMessage(err, 'Failed to pick Cin7 product') });
    } finally {
      setSaving(false);
    }
  };

  const shopifyPickOptions = useMemo(() => {
    const seen = new Set(shopifyCandidates.map((o) => o.value));
    const extra = searchOptions.filter((o) => !seen.has(o.value));
    return [...shopifyCandidates, ...extra];
  }, [shopifyCandidates, searchOptions]);

  const skuColumns = (beer) => ([
    {
      title: 'SKU',
      dataIndex: 'skuName',
      width: 220,
      render: (name, sku) => (
        <Space direction="vertical" size={0}>
          <Text>{name || sku.skuKey}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{sku.format}</Text>
        </Space>
      ),
    },
    {
      title: 'Cin7',
      key: 'cin7',
      render: (_, sku) => (
        <Cin7Cell
          sku={sku}
          saving={saving}
          onAccept={() => handleCin7Accept(beer, sku)}
          onPick={() => {
            setPickCin7({ beer, sku });
            setSearchOptions([]);
          }}
          onUnlink={() => handleCin7Unlink(beer, sku)}
        />
      ),
    },
    {
      title: 'Kounta',
      key: 'kounta',
      render: (_, sku) => (
        <KountaCell
          sku={sku}
          saving={saving}
          onAccept={() => handleKountaAccept(beer, sku)}
          onPick={() => setPickKounta({ beer, sku })}
          onUnlink={() => handleKountaUnlink(beer, sku)}
        />
      ),
    },
    {
      title: 'Shopify GPNZ',
      key: 'gpnz',
      render: (_, sku) => (
        <ShopifySkuCell
          cell={sku.shopify?.GPNZ}
          store="GPNZ"
          saving={saving}
          onAccept={() => handleShopifyAccept(beer, 'GPNZ')}
          onPick={() => openShopifyPick(beer, 'GPNZ')}
          onUnlink={() => handleShopifyUnlink(beer, 'GPNZ')}
        />
      ),
    },
    {
      title: 'Shopify GPAU',
      key: 'gpau',
      render: (_, sku) => (
        <ShopifySkuCell
          cell={sku.shopify?.GPAU}
          store="GPAU"
          saving={saving}
          onAccept={() => handleShopifyAccept(beer, 'GPAU')}
          onPick={() => openShopifyPick(beer, 'GPAU')}
          onUnlink={() => handleShopifyUnlink(beer, 'GPAU')}
        />
      ),
    },
    {
      title: 'Monday',
      key: 'monday',
      width: 140,
      render: (_, sku) => {
        const { monday } = sku;
        if (!monday?.mondayId) return <Text type="secondary">—</Text>;
        return (
          <a href={monday.href} target="_blank" rel="noopener noreferrer">
            {monday.mondayId}
          </a>
        );
      },
    },
  ]);

  const beerColumns = [
    {
      title: 'Beer',
      dataIndex: 'productName',
      render: (name, row) => (
        <Link to={`/dashboard/product-database/products/${row.productMatrixId}/edit`}>
          {name || row.productMatrixId}
        </Link>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: shopifyStatusTag,
    },
    {
      title: 'SKUs',
      dataIndex: 'skuCount',
      width: 70,
    },
    {
      title: 'Shopify GPNZ',
      key: 'gpnz',
      render: (_, beer) => (
        <ShopifyBeerCell
          summary={beer.shopify?.GPNZ}
          store="GPNZ"
          saving={saving}
          onAccept={() => handleShopifyAccept(beer, 'GPNZ')}
          onPick={() => openShopifyPick(beer, 'GPNZ')}
          onUnlink={() => handleShopifyUnlink(beer, 'GPNZ')}
        />
      ),
    },
    {
      title: 'Shopify GPAU',
      key: 'gpau',
      render: (_, beer) => (
        <ShopifyBeerCell
          summary={beer.shopify?.GPAU}
          store="GPAU"
          saving={saving}
          onAccept={() => handleShopifyAccept(beer, 'GPAU')}
          onPick={() => openShopifyPick(beer, 'GPAU')}
          onUnlink={() => handleShopifyUnlink(beer, 'GPAU')}
        />
      ),
    },
  ];

  return (
    <div id="monday-integration-platform-links-panel">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="Accept or pick Cin7, Kounta, and Shopify links per SKU. Shopify still applies to every sold format on the beer. Monday IDs are read-only. Refresh caches on the Cache tab if matches look empty."
        />
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Search beer or SKU"
            style={{ width: 240 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            value={status}
            onChange={setStatus}
            options={STATUS_OPTIONS}
            style={{ width: 160 }}
          />
          <Select
            value={needs}
            onChange={setNeeds}
            options={NEEDS_OPTIONS}
            style={{ width: 160 }}
          />
          <Button
            loading={loading}
            onClick={() => {
              loadReview();
              expandedKeys.forEach((id) => {
                loadBeerDetail(id, { force: true });
              });
            }}
          >
            Reload
          </Button>
          <Button
            type="primary"
            disabled={selectedKeys.length === 0 || saving}
            onClick={handleAcceptSelected}
          >
            Accept selected
          </Button>
        </Space>
        <Table
          rowKey="productMatrixId"
          loading={loading}
          dataSource={visibleBeers}
          columns={beerColumns}
          size="small"
          pagination={{ pageSize: 40 }}
          rowSelection={{
            selectedRowKeys: selectedKeys,
            onChange: setSelectedKeys,
          }}
          expandable={{
            expandedRowKeys: expandedKeys,
            onExpandedRowsChange: (keys) => {
              setExpandedKeys(keys);
              keys.forEach((id) => {
                loadBeerDetail(id);
              });
            },
            expandedRowRender: (beer) => {
              const detail = beerDetails[beer.productMatrixId];
              if (detail?.error) {
                return <Alert type="error" showIcon message={detail.error} />;
              }
              if (!detail?.skus || detail.loading) {
                return (
                  <div style={{ textAlign: 'center', padding: 16 }}>
                    <Spin />
                  </div>
                );
              }
              return (
                <Table
                  rowKey="skuKey"
                  dataSource={detail.skus}
                  columns={skuColumns({ ...beer, ...detail })}
                  size="small"
                  pagination={false}
                />
              );
            },
          }}
        />
      </Space>

      <Modal
        title={pickShopify
          ? `Pick Shopify product — ${pickShopify.beer.productName} (${pickShopify.store})`
          : 'Pick Shopify product'}
        visible={pickShopify != null}
        onCancel={() => {
          setPickShopify(null);
          setSearchOptions([]);
          setShopifyCandidates([]);
        }}
        footer={(
          <Button onClick={() => setPickShopify(null)}>Cancel</Button>
        )}
        width={640}
      >
        <Select
          showSearch
          filterOption={false}
          placeholder="Search or pick a candidate…"
          style={{ width: '100%' }}
          options={shopifyPickOptions}
          loading={searching || saving}
          onSearch={handleShopifySearch}
          onChange={handleShopifyPick}
          notFoundContent={searching ? 'Searching…' : 'Type to search cache'}
        />
      </Modal>

      <Modal
        title={pickCin7
          ? `Pick Cin7 product — ${pickCin7.sku.skuName || pickCin7.sku.skuKey}`
          : 'Pick Cin7 product'}
        visible={pickCin7 != null}
        onCancel={() => {
          setPickCin7(null);
          setSearchOptions([]);
        }}
        footer={(
          <Button onClick={() => setPickCin7(null)}>Cancel</Button>
        )}
        width={640}
      >
        <Select
          showSearch
          filterOption={false}
          placeholder="Search Cin7 cache by name or SKU…"
          style={{ width: '100%' }}
          options={searchOptions}
          loading={searching || saving}
          onSearch={handleCin7Search}
          onChange={handleCin7Pick}
          notFoundContent={searching ? 'Searching…' : 'Type to search cache'}
        />
      </Modal>

      <Modal
        title={pickKounta
          ? `Kounta — ${pickKounta.sku.skuName || pickKounta.sku.skuKey}`
          : 'Kounta'}
        visible={pickKounta != null}
        onCancel={() => setPickKounta(null)}
        footer={(
          <Button onClick={() => setPickKounta(null)}>Done</Button>
        )}
        width={680}
        destroyOnClose
      >
        {pickKounta ? (
          <KountaProductLinker
            skuKey={pickKounta.sku.skuKey}
            onLinksChanged={() => refreshAfterWrite(pickKounta.beer?.productMatrixId)}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default PlatformLinksPanel;
